import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCreateAdContext } from '../../context/CreateAdContext';
import { useLang } from '../../i18n/LanguageContext';
import { postRequest, getRequest } from '../../services/api/apiService';

const FIELD_LABELS = {
  name: 'Campagne',
  publisherPlatform: 'Plateforme',
  objective: 'Objectif',
  status: 'Statut',
  location: 'Localisation',
  gender: 'Genre',
  age: 'Âge',
  languages: 'Langue',
  adSetName: 'Groupe d\'annonces',
  adCreativeName: 'Nom créatif',
  creativeType: 'Format',
  adTitle: 'Titre',
  adText: 'Texte',
  description: 'Description',
  callToAction: 'CTA',
  destinationUrl: 'URL',
  budgetMeta: 'Budget Meta',
  budgetTikTok: 'Budget TikTok',
  budgetGoogle: 'Budget Google',
  budgetSnapchat: 'Budget Snapchat',
  budgetLinkedIn: 'Budget LinkedIn',
  startTime: 'Date début',
  endTime: 'Date fin',
  adAccountMeta: 'Compte Meta',
  adAccountTikTok: 'Compte TikTok',
  adAccountSnapchat: 'Compte Snapchat',
  adAccountGoogle: 'Compte Google',
  adAccountLinkedIn: 'Compte LinkedIn',
  pixelId: 'Pixel',
};

const formatActionValue = (field, value) => {
  if (Array.isArray(value)) return value.join(', ');
  if (field === 'age' && Array.isArray(value)) return `${value[0]}–${value[value.length - 1]} ans`;
  if (typeof value === 'number') return `${value} €`;
  if (typeof value === 'string' && value.includes('T')) return value.split('T')[0];
  return String(value);
};

const TypingDots = () => (
  <div className="flex items-center gap-1 px-1 py-0.5">
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        className="w-2 h-2 bg-[#221C51] rounded-full animate-bounce"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </div>
);

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: 'Bonjour, je suis Alex, votre media buyer senior IA 👔\n\n10 ans d\'expérience condensés dans ce chat : je vous guide sur le ciblage d\'audience, la création (image/vidéo générées par IA) et le bon type de campagne — et je vous explique toujours pourquoi.\n\nDécrivez-moi votre produit ou service, on construit la campagne ensemble.',
  actions: [],
};

// Suggestions de démarrage du mode plein écran (façon AdAmigo) — chaque carte
// envoie son message au clic. Les skills actifs viennent s'y ajouter en boutons.
// `bg` : dégradé vif propre à chaque action (texte blanc par-dessus).
const FULLPAGE_SUGGESTIONS = [
  { icon: '🚀', title: 'Lancer une campagne', text: 'Je veux lancer une nouvelle campagne publicitaire.',
    bg: 'bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] hover:from-[#4338CA] hover:to-[#6D28D9]' },
  { icon: '🎨', title: 'Créer une créa pub', text: 'Génère-moi une créa publicitaire (image ou vidéo) pour mon produit.',
    bg: 'bg-gradient-to-br from-[#D946EF] to-[#EC4899] hover:from-[#C026D3] hover:to-[#DB2777]' },
  { icon: '📊', title: 'Résumer mes performances', text: 'Résume mes performances publicitaires des 30 derniers jours avec les chiffres clés.',
    bg: 'bg-gradient-to-br from-[#10B981] to-[#0D9488] hover:from-[#059669] hover:to-[#0F766E]' },
  { icon: '🔍', title: 'Trouver des optimisations', text: 'Analyse mes campagnes et trouve des opportunités d\'optimisation concrètes.',
    bg: 'bg-gradient-to-br from-[#F59E0B] to-[#F97316] hover:from-[#D97706] hover:to-[#EA580C]' },
];

// embedded=true : panneau de PAGE normal (AI Launcher) — pas de bulle flottante,
// pas de drag, toujours visible, prend la hauteur/largeur de son conteneur.
// embedded=false (défaut) : comportement flottant existant, inchangé ailleurs
// (CreateAd classique, Shopify Ads).
// fullPage=true (avec embedded) : page « Chat Alex » — écran d'accueil centré
// avec suggestions + raccourcis skills tant que la conversation est vierge.
const AiChatBot = ({ defaultOpen = false, shopifyProduct = null, embedded = false, fullPage = false }) => {
  const [isOpen, setIsOpen] = useState(embedded ? true : defaultOpen);
  const productWelcome = shopifyProduct
    ? {
        role: 'assistant',
        content: `Bonjour, je suis Alex, votre media buyer senior IA 👔\n\nJ'ai détecté votre produit Shopify : **${shopifyProduct.title}**${shopifyProduct.price ? ` (${shopifyProduct.price})` : ''}.\n\nLe formulaire est déjà pré-rempli. Dites-moi sur quelle plateforme vous voulez diffuser et je vous recommande le bon type de campagne, la bonne audience et la direction créative — avec le pourquoi à chaque étape.`,
        actions: [],
      }
    : WELCOME_MESSAGE;
  const [messages, setMessages] = useState([productWelcome]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(null); // { variants: [...] } | null
  const [panelPos, setPanelPos] = useState(null); // null = default bottom-right corner
  const messagesEndRef = useRef(null);
  // Miroir local des champs qu'Alex a déjà remplis (field → value), cumulé au
  // fil de la conversation. Le backend ne reçoit que role+content dans
  // `messages` (pas les actions passées) — sans ce miroir, Alex devait
  // "redéduire" les valeurs déjà données depuis le texte à chaque tour, ce qui
  // a produit en test un champ `age` corrompu (concaténation "20212223…" au
  // lieu de [20,21,22,23]). Envoyé en `currentState` : source de vérité que
  // le backend injecte dans son prompt au lieu de laisser le modèle deviner.
  const appliedFieldsRef = useRef({});
  // Session de conversation — persistée pour que l'historique (BD) regroupe
  // les tours d'une même discussion, et pour tracer quelles campagnes créées
  // proviennent du copilote (voir logAdGeneration côté serveur).
  const sessionIdRef = useRef(localStorage.getItem('onebotads_alex_session') || null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const { dispatchActions, requestSubmit } = useCreateAdContext();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to bottom when new message arrives
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  // Focus input when panel opens; reset drag position on each open
  useEffect(() => {
    if (isOpen) {
      setPanelPos(null);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Global drag tracking
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragState.current.dragging) return;
      const dx = e.clientX - dragState.current.startX;
      const dy = e.clientY - dragState.current.startY;
      const newX = dragState.current.originX + dx;
      const newY = dragState.current.originY + dy;
      const panelW = panelRef.current?.offsetWidth || 384;
      const panelH = panelRef.current?.offsetHeight || 560;
      setPanelPos({
        x: Math.max(0, Math.min(window.innerWidth - panelW, newX)),
        y: Math.max(0, Math.min(window.innerHeight - panelH, newY)),
      });
    };
    const onMouseUp = () => { dragState.current.dragging = false; };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleDragStart = (e) => {
    if (e.target.closest('button')) return;
    const rect = panelRef.current.getBoundingClientRect();
    dragState.current = { dragging: true, startX: e.clientX, startY: e.clientY, originX: rect.left, originY: rect.top };
    e.preventDefault();
  };

  const handleGenerateCreative = useCallback(async (type, { prompt, aspectRatio }) => {
    const isImage = type === 'image';
    const field = isImage ? 'imageUrl' : 'videoUrl';
    const label = isImage ? '🖼️ Image' : '🎬 Vidéo';

    const placeholderId = Date.now();
    setMessages((prev) => [...prev, {
      role: 'assistant',
      content: `${label} en cours de génération…`,
      actions: [],
      generating: true,
      id: placeholderId,
    }]);

    const isOnCreateAd = location.pathname.includes('create-ad') || location.pathname.includes('shopify-ads');
    // Sur la page Chat Alex (fullPage), on RESTE dans la conversation — la créa
    // s'affiche inline et part quand même dans le formulaire via le provider.
    if (!isOnCreateAd && !fullPage) navigate('/dashboard/create-ad');

    try {
      const res = await postRequest('/api/creative/generate', { type, prompt, aspectRatio });
      if (!res?.url) throw new Error('URL manquante dans la réponse');

      dispatchActions([{ field, value: res.url }]);

      // L'automatisme backend a analysé le prompt et choisi le modèle — on le
      // montre à l'utilisateur (transparence sur l'usage détecté et le coût).
      const pilot = res.autoPilot
        ? `\n🎯 ${res.autoPilot.useCaseLabel ? `Usage détecté : ${res.autoPilot.useCaseLabel} · ` : ''}Modèle choisi : ${res.autoPilot.modelLabel} (${res.autoPilot.credits} crédit${res.autoPilot.credits > 1 ? 's' : ''})`
        : '';
      setMessages((prev) => prev.map((m) =>
        m.id === placeholderId
          ? {
              ...m,
              content: `${label} générée ✅ — enregistrée dans votre bibliothèque${fullPage ? '' : ' et ajoutée au formulaire'}.${pilot}`,
              generating: false,
              // Aperçu inline pour les IMAGES **ET** LES VIDÉOS (l'URL Cloudinary
              // renvoyée par /api/creative/generate est directement lisible).
              generatedUrl: res.url,
              generatedType: isImage ? 'image' : 'video',
            }
          : m
      ));
    } catch (err) {
      setMessages((prev) => prev.map((m) =>
        m.id === placeholderId
          ? { ...m, content: `❌ Échec de génération : ${err?.response?.data?.message || err.message}`, generating: false }
          : m
      ));
    }
  }, [location.pathname, navigate, dispatchActions]);

  // Soumission d'UNE campagne (bulk) — appel direct des endpoints /create-campaign,
  // sans passer par le formulaire CreateAd (pas de state React à synchroniser pour
  // N variantes). Reproduit exactement le dispatch par plateforme de handleSubmit
  // dans CreateAd.jsx : une campagne peut cibler plusieurs plateformes à la fois.
  const submitOneCampaign = useCallback(async (formData) => {
    const platforms = (formData.publisherPlatform || []).map((p) => String(p).toLowerCase());
    const endpoints = [
      { match: ['facebook', 'instagram'], url: '/api/ads/meta/create-campaign', label: 'Meta' },
      { match: ['tiktok'], url: '/api/ads/tiktok/create-campaign', label: 'TikTok' },
      { match: ['google'], url: '/api/ads/google/create-campaign', label: 'Google' },
      { match: ['snapchat'], url: '/api/ads/snapchat/create-campaign', label: 'Snapchat' },
      { match: ['linkedin'], url: '/api/ads/linkedin/create-campaign', label: 'LinkedIn' },
    ];
    const results = [];
    for (const ep of endpoints) {
      if (!ep.match.some((p) => platforms.includes(p))) continue;
      try {
        const res = await postRequest(ep.url, formData);
        results.push({ platform: ep.label, ok: true, campaignId: res?.data?.campaignId });
      } catch (err) {
        results.push({ platform: ep.label, ok: false, error: err?.response?.data?.message || err.message });
      }
    }
    return results;
  }, []);

  const handleConfirmBulk = useCallback(async () => {
    const variants = confirmBulk?.variants || [];
    setConfirmBulk(null);
    setMessages((prev) => [...prev, {
      role: 'assistant',
      content: `🚀 Lancement de ${variants.length} campagne(s) en cours…`,
      actions: [],
    }]);

    for (const variant of variants) {
      const merged = {
        ...appliedFieldsRef.current,
        ...variant,
        _source: 'chat',
        _chatSessionId: sessionIdRef.current,
      };
      let summary;
      try {
        const results = await submitOneCampaign(merged);
        summary = results.length
          ? results.map((r) => (r.ok ? `✅ ${r.platform} — ID ${r.campaignId}` : `❌ ${r.platform} — ${r.error}`)).join('\n')
          : '⚠️ Aucune plateforme reconnue pour cette variante.';
      } catch (err) {
        summary = `❌ Échec : ${err?.response?.data?.message || err.message}`;
      }
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `**${variant.name || 'Campagne'}**\n${summary}`,
        actions: [],
      }]);
    }
  }, [confirmBulk, submitOneCampaign]);

  const applyActions = useCallback((actions) => {
    if (!actions || actions.length === 0) return;

    const submitAction = actions.find((a) => a.field === '_submit');
    const bulkAction = actions.find((a) => a.field === '_submit_bulk');
    const generateImage = actions.find((a) => a.field === '_generate_image');
    const generateVideo = actions.find((a) => a.field === '_generate_video');
    const formActions = actions.filter(
      (a) => !['_submit', '_submit_bulk', '_generate_image', '_generate_video', '_suggest_actions'].includes(a.field)
    );

    // Miroir cumulé — voir commentaire sur appliedFieldsRef plus haut.
    formActions.forEach((a) => { appliedFieldsRef.current[a.field] = a.value; });

    const isOnCreateAd = location.pathname.includes('create-ad') || location.pathname.includes('shopify-ads');

    if (formActions.length > 0) {
      // Tag de provenance — best-effort : si le formulaire ignore ces champs
      // inconnus, logAdGeneration retombe simplement sur source "app".
      const taggedActions = [
        ...formActions,
        { field: '_source', value: 'chat' },
        { field: '_chatSessionId', value: sessionIdRef.current },
      ];
      if (!isOnCreateAd) {
        navigate('/dashboard/create-ad');
        setTimeout(() => dispatchActions(taggedActions), 700);
      } else {
        dispatchActions(taggedActions);
      }
    }

    if (generateImage) {
      const { prompt, aspectRatio = '1:1' } = generateImage.value || {};
      if (prompt) handleGenerateCreative('image', { prompt, aspectRatio });
    }

    if (generateVideo) {
      const { prompt, aspectRatio = '9:16' } = generateVideo.value || {};
      if (prompt) handleGenerateCreative('video', { prompt, aspectRatio });
    }

    if (submitAction) {
      if (!isOnCreateAd) {
        navigate('/dashboard/create-ad');
        setTimeout(() => setConfirmSubmit(true), 900);
      } else {
        setConfirmSubmit(true);
      }
    }

    if (bulkAction) {
      // Envoi direct aux endpoints /create-campaign (voir submitOneCampaign) —
      // pas besoin du formulaire CreateAd ni de navigation.
      const variants = Array.isArray(bulkAction.value?.variants)
        ? bulkAction.value.variants.filter((v) => v && v.name)
        : [];
      if (variants.length > 0) setConfirmBulk({ variants });
    }
  }, [location.pathname, navigate, dispatchActions, handleGenerateCreative]);

  // forcedText : envoyé par les BOUTONS D'ACTION (_suggest_actions) sous les
  // messages d'Alex — quand il vaut une string, on l'envoie tel quel au lieu
  // du champ de saisie (onClick passe un event, ignoré par le typeof).
  const sendMessage = useCallback(async (forcedText) => {
    const text = (typeof forcedText === 'string' ? forcedText : input).trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text, actions: [] };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      // Send only role + content to backend (no extra frontend metadata)
      const payload = updatedMessages.map(({ role, content }) => ({ role, content }));
      // eslint-disable-next-line no-console
      console.groupCollapsed(`%c[Alex] → /api/chat/message`, 'color:#221C51;font-weight:bold');
      console.log('messages envoyés :', payload);
      console.log('currentState (déjà rempli) :', appliedFieldsRef.current);
      console.groupEnd();

      const res = await postRequest('/api/chat/message', {
        messages: payload,
        shopifyProduct,
        currentState: appliedFieldsRef.current,
        accountScope: accountScopeRef.current,
        sessionId: sessionIdRef.current,
      });
      if (res.sessionId && res.sessionId !== sessionIdRef.current) {
        sessionIdRef.current = res.sessionId;
        localStorage.setItem('onebotads_alex_session', res.sessionId);
      }

      // eslint-disable-next-line no-console
      console.groupCollapsed(`%c[Alex] ← réponse`, 'color:#16a34a;font-weight:bold');
      console.log('message :', res.message);
      console.log('actions :', res.actions);
      console.groupEnd();

      const botMsg = {
        role: 'assistant',
        content: res.message,
        actions: res.actions || [],
      };
      setMessages((prev) => [...prev, botMsg]);

      if (res.actions && res.actions.length > 0) {
        applyActions(res.actions);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Alex] ✗ erreur /api/chat/message', {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      const errMsg = err?.response?.data?.message || 'Une erreur est survenue. Vérifiez votre clé GROQ_API_KEY.';
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `❌ ${errMsg}`, actions: [] },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, applyActions]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = () => {
    setMessages([WELCOME_MESSAGE]);
    setInput('');
    appliedFieldsRef.current = {};
    sessionIdRef.current = null;
    localStorage.removeItem('onebotads_alex_session');
    setShowHistory(false);
  };

  // ── Périmètre de compte du chat (mode plein écran) ─────────────
  // "🌐 Tous les comptes" (défaut) ou un compte publicitaire précis : le choix
  // est envoyé au backend (accountScope) qui cadre Alex sur ce périmètre —
  // stats, campagnes, plans et recommandations se concentrent dessus.
  const [scopeOptions, setScopeOptions] = useState([]);
  const [accountScope, setAccountScope] = useState(null); // null = tous
  // Miroir en ref : lu par sendMessage (useCallback) sans dépendance instable.
  const accountScopeRef = useRef(null);
  const applyScope = (scope) => { accountScopeRef.current = scope; setAccountScope(scope); };
  useEffect(() => {
    if (!fullPage) return;
    getRequest('/api/connected-platforms/accounts-status')
      .then((res) => {
        const opts = (res.platforms || [])
          .filter((p) => p.connected && p.accountId)
          .map((p) => ({ platform: p.platform, accountId: p.accountId, label: `${p.platform} · ${String(p.accountId).slice(0, 14)}` }));
        setScopeOptions(opts);
      })
      .catch(() => {});
  }, [fullPage]);

  // ── Raccourcis skills du mode plein écran ──────────────────────
  const [skillShortcuts, setSkillShortcuts] = useState([]);
  useEffect(() => {
    if (!fullPage) return;
    getRequest('/api/skills')
      .then((res) => {
        const actives = (res.skills || []).filter((s) => s.isActive);
        // Un skill par catégorie clé, 6 max — évite 63 boutons.
        const seen = new Set();
        const picked = [];
        for (const cat of ['strategy', 'diagnostics', 'creative', 'funnel', 'reporting', 'custom']) {
          const s = actives.find((x) => x.category === cat && !seen.has(x.id));
          if (s) { seen.add(s.id); picked.push(s); }
          if (picked.length >= 6) break;
        }
        setSkillShortcuts(picked);
      })
      .catch(() => {});
  }, [fullPage]);

  // ── Historique des conversations ────────────────────────────────
  // Le backend persiste chaque tour (chat_messages, voir chatRoutes.js) —
  // ce panneau liste les sessions passées et recharge leur transcript.
  const [showHistory, setShowHistory] = useState(false);
  const [historySessions, setHistorySessions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const toggleHistory = async () => {
    const opening = !showHistory;
    setShowHistory(opening);
    if (!opening) return;
    setHistoryLoading(true);
    try {
      const res = await getRequest('/api/chat/sessions');
      setHistorySessions(res.sessions || []);
    } catch {
      setHistorySessions([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Politique d'ouverture du chat : CHAQUE ouverture démarre une conversation
  // VIERGE (l'ancien chat est nettoyé — il reste consultable dans le menu
  // « Chat History »). Exception : /dashboard/alex?session=ID (bouton
  // « Continuer la conversation » de Chat History) recharge le transcript
  // complet de cette session et la discussion reprend où elle s'était arrêtée.
  // En plein écran, on précharge aussi la liste des conversations passées
  // pour l'écran d'accueil (« Reprendre une conversation »).
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (fullPage) {
      getRequest('/api/chat/sessions')
        .then((res) => setHistorySessions(res.sessions || []))
        .catch(() => {});
    }
    const urlSession = fullPage ? new URLSearchParams(window.location.search).get('session') : null;
    if (!urlSession) {
      // Nouveau chat : on repart de zéro (une nouvelle session sera créée au
      // premier message — l'ancienne reste intacte dans l'historique).
      sessionIdRef.current = null;
      localStorage.removeItem('onebotads_alex_session');
      return;
    }
    getRequest(`/api/chat/sessions/${urlSession}/messages`)
      .then((res) => {
        const rows = (res.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
          actions: Array.isArray(m.actions) ? m.actions : [],
        }));
        if (rows.length > 0) {
          setMessages(rows);
          sessionIdRef.current = urlSession;
          localStorage.setItem('onebotads_alex_session', urlSession);
          const mirror = {};
          rows.forEach((m) => (m.actions || []).forEach((a) => {
            if (a.field && !String(a.field).startsWith('_')) mirror[a.field] = a.value;
          }));
          appliedFieldsRef.current = mirror;
        }
      })
      .catch(() => { /* session introuvable — chat vierge */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openHistorySession = async (session) => {
    setHistoryLoading(true);
    try {
      const res = await getRequest(`/api/chat/sessions/${session.sessionId}/messages`);
      const rows = (res.messages || []).map((m) => ({
        role: m.role,
        content: m.content,
        actions: Array.isArray(m.actions) ? m.actions : [],
      }));
      if (rows.length > 0) {
        setMessages(rows);
        sessionIdRef.current = session.sessionId;
        localStorage.setItem('onebotads_alex_session', session.sessionId);
        // Reconstruit le miroir des champs remplis depuis les actions passées,
        // pour que la conversation reprenne avec le bon état de formulaire.
        const mirror = {};
        rows.forEach((m) => (m.actions || []).forEach((a) => {
          if (a.field && !String(a.field).startsWith('_')) mirror[a.field] = a.value;
        }));
        appliedFieldsRef.current = mirror;
      }
      setShowHistory(false);
    } catch {
      /* transcript indisponible — on reste sur la conversation courante */
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <>
      {/* ── Keyframe animations ── */}
      <style>{`
        @keyframes robotFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes robotGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,28,81,0.4); }
          50% { box-shadow: 0 0 0 10px rgba(34,28,81,0); }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .robot-float { animation: robotFloat 3s ease-in-out infinite, robotGlow 3s ease-in-out infinite; }
        .chat-panel { animation: slideInRight 0.3s ease-out; }
        .message-text { white-space: pre-wrap; word-break: break-word; }
      `}</style>

      <div className={embedded ? "h-full w-full flex flex-col" : "fixed bottom-24 right-4 md:bottom-6 md:right-6 z-50 flex flex-col items-end"}>

        {/* ── Chat Panel ──
            embedded : panneau de page fixe (AI Launcher desktop/tablette).
            flottant + mobile (< md) : bottom sheet plein écran (pas de drag —
            le tactile ne déclenche pas handleDragStart, donc autant assumer
            visuellement une prise en main plein écran, plus lisible qu'une
            carte 384px qui déborderait de la plupart des téléphones).
            flottant + desktop (md+) : carte flottante draggable, inchangée. */}
        {isOpen && (
          <div
            ref={panelRef}
            className={
              embedded
                ? "chat-panel w-full h-full flex flex-col bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                : `chat-panel z-50 flex flex-col bg-white shadow-2xl border border-gray-100 overflow-hidden
                   fixed inset-x-0 bottom-0 w-full h-[80vh] rounded-t-2xl
                   md:inset-auto md:bottom-24 md:right-6 md:w-96 md:h-[560px] md:rounded-2xl`
            }
            style={
              embedded
                ? undefined
                : panelPos
                  ? { position: 'fixed', top: panelPos.y, left: panelPos.x, height: '560px', zIndex: 50 }
                  : undefined
            }
          >
            {/* Header — épuré en plein écran (fond blanc, actions discrètes),
                barre marine conservée pour la bulle flottante / panneau AI Launcher. */}
            <div
              className={`flex-shrink-0 flex items-center gap-3 select-none ${
                fullPage
                  ? 'bg-white border-b border-gray-100 px-4 py-2.5'
                  : `bg-[#221C51] px-4 py-3 ${embedded ? '' : 'md:cursor-grab md:active:cursor-grabbing'}`
              }`}
              onMouseDown={embedded ? undefined : handleDragStart}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-lg flex-shrink-0 ${fullPage ? 'bg-[#221C51]/5' : 'bg-white/10'}`} title="Alex — Media Buyer Senior IA">
                👔
              </div>
              <div className="min-w-0">
                <p className={`font-bold text-sm leading-tight ${fullPage ? 'text-[#221C51]' : 'text-white'}`}>Alex</p>
                {!fullPage && (
                  <p className="text-purple-300 text-xs">
                    {embedded ? 'Guide audience · créa · type de campagne' : (panelPos ? '↕ Faites glisser pour déplacer' : 'Guide audience · créa · type de campagne')}
                  </p>
                )}
              </div>
              <div className="ml-auto flex items-center gap-1.5">
                {/* Périmètre : tous les comptes ou un compte pub précis */}
                {fullPage && scopeOptions.length > 0 && (
                  <select
                    value={accountScope ? `${accountScope.platform}|${accountScope.accountId}` : 'all'}
                    onChange={(e) => {
                      if (e.target.value === 'all') return applyScope(null);
                      const opt = scopeOptions.find((o) => `${o.platform}|${o.accountId}` === e.target.value);
                      applyScope(opt || null);
                    }}
                    title="Périmètre d'analyse d'Alex : tous les comptes ou un compte publicitaire précis"
                    className="text-[11px] font-medium bg-gray-50 text-[#221C51] border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none max-w-[150px] cursor-pointer hover:border-gray-300 transition"
                  >
                    <option value="all">🌐 Tous les comptes</option>
                    {scopeOptions.map((o) => (
                      <option key={`${o.platform}|${o.accountId}`} value={`${o.platform}|${o.accountId}`}>
                        🎯 {o.label}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  onClick={toggleHistory}
                  title="Historique des conversations"
                  className={`text-xs px-2 py-1.5 rounded-lg transition whitespace-nowrap ${
                    fullPage
                      ? showHistory ? 'bg-gray-100 text-[#221C51]' : 'text-gray-400 hover:text-[#221C51] hover:bg-gray-50'
                      : showHistory ? 'bg-white/20 text-white' : 'text-purple-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  🕘{fullPage && <span className="ml-1 font-medium hidden sm:inline">{t('Historique')}</span>}
                </button>
                <button
                  onClick={resetChat}
                  title="Nouvelle conversation"
                  className={`text-xs px-2 py-1.5 rounded-lg transition ${fullPage ? 'text-gray-400 hover:text-[#221C51] hover:bg-gray-50' : 'text-purple-300 hover:text-white hover:bg-white/10'}`}
                >
                  ↺
                </button>
                {!embedded && (
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-purple-300 hover:text-white text-lg leading-none hover:bg-white/10 w-7 h-7 flex items-center justify-center rounded transition"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* ── Panneau Historique (recouvre la zone messages) ── */}
            {showHistory && (
              <div className="flex-1 overflow-y-auto bg-gray-50 px-4 py-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-[#221C51] uppercase tracking-wide">🕘 Conversations passées</p>
                  <button onClick={resetChat} className="text-xs font-semibold text-white bg-[#221C51] rounded-lg px-2.5 py-1.5 hover:bg-[#2d2570]">
                    ➕ Nouvelle
                  </button>
                </div>
                {historyLoading ? (
                  <p className="text-xs text-gray-400 text-center py-8">Chargement…</p>
                ) : historySessions.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-8">
                    Aucune conversation enregistrée pour l'instant — elles apparaîtront ici automatiquement.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {historySessions.map((s) => (
                      <button
                        key={s.sessionId}
                        onClick={() => openHistorySession(s)}
                        className={`w-full text-left bg-white border rounded-xl px-3 py-2.5 hover:border-[#221C51]/40 transition ${
                          sessionIdRef.current === s.sessionId ? 'border-[#221C51]/60 ring-1 ring-[#221C51]/20' : 'border-gray-200'
                        }`}
                      >
                        <p className="text-xs text-[#221C51] font-semibold line-clamp-2">{s.preview || 'Conversation'}</p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {new Date(s.lastAt).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          {' · '}{s.messageCount} message{s.messageCount > 1 ? 's' : ''}
                          {sessionIdRef.current === s.sessionId ? ' · en cours' : ''}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Écran d'accueil plein écran — épuré façon AdAmigo : avatar,
                titre, 4 cartes, point final. Les skills restent accessibles
                via un lien replié ; l'historique vit dans le menu Chat History. ── */}
            {fullPage && !showHistory && messages.length <= 1 && !loading && (
              <div className="flex-1 overflow-y-auto bg-white flex flex-col items-center justify-center px-4 py-8">
                <div className="w-14 h-14 rounded-full bg-[#221C51] flex items-center justify-center text-2xl mb-5 shadow-md">👔</div>
                <h2 className="text-xl md:text-2xl font-bold text-[#221C51]">{t('Nouveau chat')}</h2>
                <p className="text-sm text-gray-400 mt-1.5 mb-8">{t("Comment puis-je vous aider aujourd'hui ?")}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-lg">
                  {FULLPAGE_SUGGESTIONS.map((s) => (
                    <button
                      key={s.title}
                      onClick={() => sendMessage(s.text)}
                      className={`${s.bg} rounded-xl px-4 py-4 text-left shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all`}
                    >
                      <span className="text-lg mr-2 align-middle">{s.icon}</span>
                      <span className="text-sm font-semibold text-white align-middle">{t(s.title)}</span>
                    </button>
                  ))}
                </div>

                {/* Skills d'expert — repliés par défaut pour garder l'écran léger */}
                {skillShortcuts.length > 0 && (
                  <details className="w-full max-w-lg mt-6 group">
                    <summary className="text-xs text-gray-400 hover:text-[#221C51] cursor-pointer text-center list-none transition select-none">
                      {t("🧠 Appliquer un skill d'expert")} <span className="group-open:hidden">▾</span><span className="hidden group-open:inline">▴</span>
                    </summary>
                    <div className="flex flex-wrap justify-center gap-2 mt-3">
                      {skillShortcuts.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => sendMessage(`Applique le skill « ${s.title} » à mon compte — pose-moi les questions nécessaires une par une avec des boutons de réponse.`)}
                          title={s.summary || s.title}
                          className="text-xs font-medium bg-white border border-gray-200 rounded-full px-3.5 py-2 text-[#221C51] hover:border-[#221C51]/40 hover:shadow-sm transition"
                        >
                          {s.title}
                        </button>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}

            {/* Messages — fond blanc en plein écran (épuré), gris clair en bulle */}
            <div className={`flex-1 overflow-y-auto px-4 py-4 space-y-4 ${fullPage ? 'bg-white' : 'bg-gray-50'} ${showHistory || (fullPage && messages.length <= 1 && !loading) ? 'hidden' : ''}`}>
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-full bg-[#221C51] flex items-center justify-center text-sm flex-shrink-0 mt-0.5">
                      👔
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5 max-w-[78%]">
                    <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-[#221C51] text-white rounded-br-sm'
                        : 'bg-white text-[#374151] border border-gray-200 rounded-bl-sm shadow-sm'
                    }`}>
                      <span className="message-text">{msg.content}</span>
                      {msg.generating && (
                        <span className="inline-block ml-2 align-middle">
                          <svg className="animate-spin w-3.5 h-3.5 text-[#221C51]" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                          </svg>
                        </span>
                      )}
                    </div>
                    {msg.generatedUrl && msg.generatedType !== 'video' && (
                      <img
                        src={msg.generatedUrl}
                        alt="Generated creative"
                        className="mt-1.5 rounded-xl border border-gray-200 max-w-full shadow-sm"
                        style={{ maxHeight: '180px', objectFit: 'cover' }}
                      />
                    )}
                    {msg.generatedUrl && msg.generatedType === 'video' && (
                      <video
                        src={msg.generatedUrl}
                        controls
                        playsInline
                        preload="metadata"
                        className="mt-1.5 rounded-xl border border-gray-200 max-w-full shadow-sm bg-black"
                        style={{ maxHeight: '240px' }}
                      />
                    )}

                    {/* Action chips (les champs remplis) — les actions "boutons"
                        (_suggest_actions) sont rendues à part ci-dessous. */}
                    {msg.actions && msg.actions.filter((a) => a.field !== '_suggest_actions').length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {msg.actions.filter((a) => a.field !== '_suggest_actions').map((action, j) => (
                          <span key={j} className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5 font-medium">
                            <span className="text-green-500">✓</span>
                            {FIELD_LABELS[action.field] || action.field}
                            {action.value !== undefined && (
                              <span className="text-green-600 font-semibold">
                                : {formatActionValue(action.field, action.value)}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Boutons d'action proposés par Alex (_suggest_actions) —
                        le clic envoie la commande du bouton comme message. */}
                    {(() => {
                      const suggest = (msg.actions || []).find((a) => a.field === '_suggest_actions');
                      const buttons = Array.isArray(suggest?.value) ? suggest.value.filter((b) => b && (b.command || b.label)) : [];
                      if (buttons.length === 0) return null;
                      return (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {buttons.map((b, j) => (
                            <button
                              key={b.key || j}
                              onClick={() => sendMessage(b.command || b.label)}
                              disabled={loading}
                              className="text-xs font-semibold bg-[#221C51] text-white rounded-xl px-3 py-1.5 hover:bg-[#2d2570] transition disabled:opacity-50 shadow-sm"
                            >
                              {b.label || b.command}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {loading && (
                <div className="flex justify-start gap-2">
                  <div className="w-7 h-7 rounded-full bg-[#221C51] flex items-center justify-center text-sm flex-shrink-0">
                    👔
                  </div>
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm shadow-sm px-4 py-3">
                    <TypingDots />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input — plein écran : grande barre arrondie centrée façon AdAmigo ;
                bulle/panneau : barre compacte historique. */}
            <div className={`flex-shrink-0 bg-white ${fullPage ? 'px-4 pb-4 pt-2' : 'px-3 py-3 border-t border-gray-200'}`}
              style={embedded ? undefined : { paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
              <div className={fullPage ? 'max-w-2xl mx-auto' : ''}>
                <div className={`flex gap-2 items-end ${fullPage ? 'border border-gray-200 rounded-2xl px-3 py-2 shadow-sm focus-within:border-[#221C51]/40 transition bg-white' : ''}`}>
                  <textarea
                    ref={inputRef}
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={fullPage ? t('Demandez à Alex…') : 'Décrivez votre campagne...'}
                    disabled={loading}
                    className={`flex-1 text-sm resize-none disabled:opacity-50 transition focus:outline-none ${
                      fullPage
                        ? 'px-1 py-2 border-none bg-transparent'
                        : 'px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:border-[#221C51]'
                    }`}
                    style={{ minHeight: '38px', maxHeight: '100px' }}
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
                    }}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                    className={`flex-shrink-0 w-10 h-10 bg-[#221C51] text-white flex items-center justify-center hover:bg-[#2d267a] disabled:opacity-40 disabled:cursor-not-allowed transition ${fullPage ? 'rounded-full mb-0.5' : 'rounded-xl'}`}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 mt-2 text-center">
                  {fullPage
                    ? t('Alex peut faire des erreurs. Vérifiez les informations importantes.')
                    : 'Entrée pour envoyer · Shift+Entrée pour nouvelle ligne'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Submit confirmation modal ── */}
        {confirmSubmit && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-80 flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#221C51] rounded-full flex items-center justify-center text-xl flex-shrink-0">🚀</div>
                <div>
                  <p className="font-bold text-[#221C51] text-sm leading-tight">Lancer la campagne ?</p>
                  <p className="text-gray-500 text-xs mt-0.5">Le formulaire sera soumis avec les données actuelles.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setConfirmSubmit(false);
                    requestSubmit();
                    setMessages((prev) => [...prev, {
                      role: 'assistant',
                      content: '🚀 Lancement en cours… Vérifiez le formulaire pour le résultat.',
                      actions: [],
                    }]);
                  }}
                  className="flex-1 bg-[#221C51] text-white text-sm font-semibold py-2 rounded-xl hover:bg-[#2d267a] transition"
                >
                  Confirmer
                </button>
                <button
                  onClick={() => setConfirmSubmit(false)}
                  className="flex-1 bg-gray-100 text-gray-600 text-sm font-semibold py-2 rounded-xl hover:bg-gray-200 transition"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Bulk submit confirmation modal ── */}
        {confirmBulk && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-96 flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#221C51] rounded-full flex items-center justify-center text-xl flex-shrink-0">🚀</div>
                <div>
                  <p className="font-bold text-[#221C51] text-sm leading-tight">
                    Lancer {confirmBulk.variants.length} campagnes ?
                  </p>
                  <p className="text-gray-500 text-xs mt-0.5">Chaque variante hérite des champs déjà remplis.</p>
                </div>
              </div>
              <ul className="max-h-40 overflow-y-auto text-xs text-gray-600 flex flex-col gap-1 bg-gray-50 rounded-lg p-2">
                {confirmBulk.variants.map((v, i) => (
                  <li key={i} className="truncate">• {v.name}</li>
                ))}
              </ul>
              <div className="flex gap-2">
                <button
                  onClick={handleConfirmBulk}
                  className="flex-1 bg-[#221C51] text-white text-sm font-semibold py-2 rounded-xl hover:bg-[#2d267a] transition"
                >
                  Confirmer
                </button>
                <button
                  onClick={() => setConfirmBulk(null)}
                  className="flex-1 bg-gray-100 text-gray-600 text-sm font-semibold py-2 rounded-xl hover:bg-gray-200 transition"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Robot Button (flottant uniquement — en mode embedded le panneau est toujours visible) ── */}
        {!embedded && (
          <button
            onClick={() => setIsOpen((o) => !o)}
            className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl shadow-lg transition-all duration-200 hover:scale-110 ${
              isOpen ? 'bg-[#0F0B2D] scale-110' : 'bg-[#221C51] robot-float'
            }`}
            title="Ouvrir l'assistant IA"
          >
            🤖
          </button>
        )}
      </div>
    </>
  );
};

export default AiChatBot;
